import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { withRouter } from "tools/routerCompat";
import { format, parseISO, isValid } from "date-fns";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import * as yup from "yup";
import RenderDesignations from "components/Appraiser/RenderDesignations";
import RenderLicenses from "components/Appraiser/RenderLicenses";
import AppraiserSignature from "components/Appraiser/AppraiserSignature";
import { UserInformation, LoginActivity, AppraiserMacCard } from "components/User";
import EmployeeTEAM from "components/Employee/EmployeeTEAM";
import { MSpinner } from "components/Shared";
import MCard from "components/Shared/MCard.tsx";
import { DATA_TYPE_DROPDOWN, DATA_TYPE_BOOLEAN, DATA_TYPE_DATE, DATA_TYPE_PHONE_NUMBER, DATA_TYPE_NUMBER } from "typeConstants/dataTypes";
import MGrid, { COL_CODE_STATE, FILTER_TYPE_NONE } from "components/Shared/MGrid.tsx";
import { updateHeader, clearHeader } from "actions/layout";
import { getNewSearchResults } from "actions/search";
import { phoneRegExp } from "components/Shared/Formatters";
import ExclusionaryList from "components/Shared/ExclusionaryList";
import { updateAppraiserProfile } from "actions/appraiser";
import { getAppraiserCounties, setAppraiserCounties } from "actions/appraiserCounties";
import {
  APPRAISER_EDIT,
  APPRAISER_LICENSE_EDIT,
  APPRAISER_COUNTY_PROFICIENCY_EDIT,
  APPRAISER_LICENSE_VIEW,
  APPRAISER_SIGNATURE_VIEW,
  CUSTOMER_APPRAISER_EXCLUSION_MAINTENANCE,
  AUTHENTICATION_VIEW,
  AUTHENTICATION_EDIT,
  APPRAISER_COUNTY_FILES_EDIT,
  APPRAISER_COUNTY_FILES_VIEW,
  APPRAISER_INACTIVE_EDIT,
  MANAGEMENT_CALENDAR_EDIT,
} from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import { getPayRateTypeLabel } from "typeConstants/payrateTypes";
import AppraiserCountyFileManager from "components/Appraiser/AppraiserCountyFileManager";
import { getAllCounties } from "actions/region";
import EmployeeCalendar from "components/Employee/EmployeeCalendar.tsx";
import AdminTimeLog from "components/Shared/AdminTimeLog";
import AppraiserQueue from "components/Appraiser/AppraiserQueue.tsx";

class AppraiserProfileEdit extends React.Component {
  constructor(props) {
    super(props);
    this.displayName = "AppraiserProfileEdit";

    this.viewOrdersRedirect = this.viewOrdersRedirect.bind(this);
    this.viewHistoryRedirect = this.viewHistoryRedirect.bind(this);
    this.handleMCardSave = this.handleMCardSave.bind(this);
    this.getAppraiserCountyFields = this.getAppraiserCountyFields.bind(this);
    this.onAppraiserCountyEditStarted = this.onAppraiserCountyEditStarted.bind(this);
    this.onAppraiserCountyEditFinished = this.onAppraiserCountyEditFinished.bind(this);
    this.onAppraiserCountyEditCancelled = this.onAppraiserCountyEditCancelled.bind(this);
    this.shouldCancelEdit = this.shouldCancelEdit.bind(this);
    this.countyRowSelectionChanged = this.countyRowSelectionChanged.bind(this);
    this.getSortedCounties = this.getSortedCounties.bind(this);
    this.getSortedAppraiserCounties = this.getSortedAppraiserCounties.bind(this);
    this.getSelectedCounties = this.getSelectedCounties.bind(this);
    this.areListsEqual = this.areListsEqual.bind(this);
    this.setControlMethods = this.setControlMethods.bind(this);
    this.submitClicked = this.submitClicked.bind(this);
    this.getHeaderDefinitions = this.getHeaderDefinitions.bind(this);
    this.backToAppraiserList = this.backToAppraiserList.bind(this);

    this.mCardControlMethods = {};
    this.state = {
      createAppraiserButtonDisabled: true,
      createdAppraiser: props.appraiser,
      isEditingCounties: false,
      appraiserCounties: this.getSortedAppraiserCounties(props.appraiserCounties),
      allCounties: [],
      selectedCounties: [],
      originalSelectedCounties: [],
      lastAppraiserCountyGridDataUpdate: null
    };
  }

  componentDidMount() {
    const { updateHeader, showHeaderButton } = this.props;
    if (showHeaderButton) updateHeader(this.getHeaderDefinitions());
  }

  componentDidUpdate(prevProps) {
    if (
      (!this.props.fetchingAppraiserCounties &&
        prevProps.fetchingAppraiserCounties) ||
      !this.areListsEqual(
        this.props.appraiserCounties,
        prevProps.appraiserCounties,
        (c1, c2) => c1.id === c2.id
      )
    ) {
      let sortedCounties = this.getSortedAppraiserCounties(this.props.appraiserCounties);
      this.setState({
        appraiserCounties: sortedCounties,
        lastAppraiserCountyGridDataUpdate: Date.now()
      });
    }

    if (!this.props.fetchingAllCounties && prevProps.fetchingAllCounties) {
      let sortedCounties = this.getSortedCounties(this.props.allCounties);
      let selectedCounties = this.getSelectedCounties(
        this.state.appraiserCounties,
        sortedCounties
      );
      this.setState({
        allCounties: sortedCounties,
        selectedCounties: selectedCounties,
        originalSelectedCounties: selectedCounties
      });
    }
  }

  componentWillUnmount() {
    if (this.props.showHeaderButton) this.props.clearHeader();
  }

  getHeaderDefinitions() {
    const { creatingAppraiser } = this.props;
    if (!creatingAppraiser)
      return {
        dropdownTitle: "Appraiser Profile",
        buttons: [
          {
            name: "View Order Queue",
            onClick: this.viewOrdersRedirect
          },
          {
            name: "View Full History",
            onClick: this.viewHistoryRedirect
          }
        ]
      };
  }

  setControlMethods(key) {
    return cm => (this.mCardControlMethods[key] = cm);
  }

  areListsEqual(l1, l2, comparer) {
    l1 = l1 || [];
    l2 = l2 || [];

    if (l1.length !== l2.length) return false;

    return !l1.some(l1e => !l2.some(l2e => comparer(l1e, l2e)));
  }

  getSortedCounties(counties) {
    if (!counties) return null;
    return [...counties].sort((c1, c2) =>
      c1.state === c2.state
        ? c1.name.localeCompare(c2.name)
        : c1.state.localeCompare(c2.state)
    );
  }

  //appraiser counties now has different model than counties so we need a different function for it
  getSortedAppraiserCounties(counties) {
    if (!counties) return null;
    return [...counties].sort((c1, c2) =>
      c1.county.state === c2.county.state
        ? c1.county.name.localeCompare(c2.county.name)
        : c1.county.state.localeCompare(c2.county.state)
    );
  }

  getSelectedCounties(appraiserCounties, allCounties) {
    if (!appraiserCounties) appraiserCounties = this.state.appraiserCounties;
    if (!allCounties) allCounties = this.state.allCounties;

    return allCounties.reduce((acc, county, i) => {
      if (
        appraiserCounties.some(
          ac => ac.county.state === county.state && ac.county.name === county.name
        )
      ) {
        acc.push(i);
      }
      return acc;
    }, []);
  }

  viewOrdersRedirect() {
    this.props.history.push(`/appraiser-cases/${this.props.appraiser.appraiserId}`);
  }

  viewHistoryRedirect() {
    this.props.getNewSearchResults(
      // search on appraiser number
      "AN_" + this.props.appraiser.appraiserNumber
    );
    this.props.history.push("/search-results");
  }

  submitClicked() {
    let hasValidationErrors = false;
    Object.keys(this.mCardControlMethods).forEach(k => {
      if (!this.mCardControlMethods[k].runValidation(true))
        hasValidationErrors = true;
    });
    if (!hasValidationErrors) {
      this.props.onSave(this.state.createdAppraiser);
    }
  }

  handleMCardSave(arr, obj) {
    const { addressGeocode } = obj;
    let ob;
    if (addressGeocode !== undefined) {
      const [addressLatitude, addressLongitude] = addressGeocode.split(", ");
      ob = { ...obj, addressLatitude, addressLongitude };
      delete ob.addressGeocode;
    } else {
      ob = obj;
    }

    if (this.props.creatingAppraiser) {
      let newObj = { ...this.state.createdAppraiser, ...ob };

      this.setState({ createdAppraiser: newObj });

      if (
        ob.hasOwnProperty("appraiserDesignationViewModels") ||
        ob.hasOwnProperty("appraiserLicenseViewModels")
      ) {
        this.props.onSave(newObj);
      }
    } else {
      let newObj = { ...this.props.appraiser, ...ob };
      this.props.onSave(newObj);
    }
  }

  countyRowSelectionChanged(selected) {
    this.setState({ selectedCounties: selected });
  }

  getAppraiserCountyFields() {
    if (!this.state.appraiserCounties) return [];

    let colDefs = [
      { code: COL_CODE_STATE, dataSource: "county.state" },
      { key: "county", name: "County", dataSource: "county.name" },
      { key: "date", name: "Date", dataSource: "createdDate", dataType: DATA_TYPE_DATE },
      { key: "user", name: "User", dataSource: "createdByInternetUser.username" },
    ];

    if (this.state.isEditingCounties) {
      colDefs = [
        { code: COL_CODE_STATE, dataSource: "state" },
        { key: "county", name: "County", dataSource: "name" },
      ];
    }

    let counties;

    if (this.state.isEditingCounties) {
      counties = this.state.allCounties;
    } else {
      counties = this.state.appraiserCounties;
      colDefs.forEach(cd => (cd.filterType = FILTER_TYPE_NONE));
    }

    return [
      {
        key: "grid",
        control: (
          <MGrid
            columnDefinitions={colDefs}
            data={counties}
            dataLastChanged={this.state.lastAppraiserCountyGridDataUpdate}
            {...this.state.isEditingCounties && {
              allowRowSelection: true,
              onRowSelectionChanged: this.countyRowSelectionChanged,
              selectedRows: this.state.selectedCounties
            }}
          />
        )
      }
    ];
  }

  onAppraiserCountyEditStarted() {
    if (
      (this.state.allCounties === null || !this.state.allCounties.length) &&
      !this.props.fetchingAllCounties
    ) {
      this.props.getAllCounties();
      this.setState({ isEditingCounties: true });
    } else {
      let selectedCounties = this.getSelectedCounties();
      this.setState({
        isEditingCounties: true,
        selectedCounties: selectedCounties,
        originalSelectedCounties: selectedCounties
      });
    }
  }

  onAppraiserCountyEditFinished() {
    this.props.saveAppraiserCounties(
      this.props.appraiser.appraiserId,
      this.state.allCounties.filter((c, i) =>
        this.state.selectedCounties.some(j => j === i)
      ),
      () => getAppraiserCounties(this.props.appraiser.appraiserId),
    );
    this.onAppraiserCountyEditCancelled();
  }

  shouldCancelEdit() {
    return this.areListsEqual(
      this.state.originalSelectedCounties,
      this.state.selectedCounties,
      (i1, i2) => i1 === i2
    );
  }

  onAppraiserCountyEditCancelled() {
    this.setState({ isEditingCounties: false });
  }

  backToAppraiserList() {
    const {
      history,
      location: {
        state: {
          searchText
        }
      }
    } = this.props;
    history.push({
      pathname: "/appraiser",
      state: {
        searchText,
      },
    });
  }

  render() {
    const {
      isFetching,
      appraiser,
      appraiserManagers,
      stateList,
      creatingAppraiser,
      isFetchingInternetUser,
      internetUser,
      verticalDisplay,
      canEditAppraiser,
      canEditAppraiserLicense,
      canEditAppraiserCountyProficiency,
      canViewAppraiserSignature,
      canViewAppraiserLicense,
      customerAppraiserExclusionMaintenance,
      canViewUserInfo,
      canEditUserInfo,
      canEditInactiveDate,
      canEditCalendarAsManager,
    } = this.props;

    if (isFetching || appraiser === null) {
      return <MSpinner />;
    }

    const inactive = validateDate(appraiser.inactiveDate); // convert to string and avoid 0001-01-01
    const ASDAEffectiveDate = validateDate(
      appraiser.appraisalSubcommitteeDisciplinaryActionEffectiveDate
    );
    const ASDAEndDate = validateDate(
      appraiser.appraisalSubcommitteeDisciplinaryActionEndDate
    );
    const allowEditsEffectiveDate = validateDate(
      appraiser.allowEditsEffectiveDate
    );
    const allowEditsEndDate = validateDate(appraiser.allowEditsEndDate);
    const hireDate = validateDate(appraiser.hireDate);
    const activeDate = validateDate(appraiser.activeDate);
    const terminatedDate = validateDate(appraiser.terminatedDate);
    let statusMessage = "Active";
    const today = new Date();
    if (appraiser.appraiserId === 0) {
      statusMessage = "New";
    }
    else if (terminatedDate !== null && new Date(appraiser.terminatedDate) <= today) {
      statusMessage = "Terminated";
    }
    else if (inactive !== null && new Date(appraiser.inactiveDate) <= today) {
      statusMessage = "Inactive";
    }

    const cellWidth = verticalDisplay ? 12 : 4;
    return (
      <Grid container gap={1}>
        <Grid size={cellWidth}>
          <MCard title="General" getControlMethods={this.setControlMethods("general")} fields={[
            { key: "appraiserNumber", label: "Appraiser #", value: appraiser.appraiserNumber, editable: creatingAppraiser },
            { key: "status", label: "Status", value: statusMessage, editable: false },
            { key: "firstName", label: "First Name", value: appraiser.firstName, validation: yup.string().nullable().required("First Name is required") },
            { key: "middleName", label: "Middle Name", value: appraiser.middleName },
            { key: "lastName", label: "Last Name", value: appraiser.lastName, validation: yup.string().nullable().required("Last Name is required") },
            { key: "nameSuffix", label: "Suffix", value: appraiser.nameSuffix },
            { key: "addressGeocode", label: "Geocode", value: `${appraiser.addressLatitude}, ${appraiser.addressLongitude}`, editable: true }
          ]} isEditable={canEditAppraiser} onEditFinished={this.handleMCardSave} onlyEditable={creatingAppraiser}
          /></Grid>
        <Grid size={cellWidth}>
          <MCard title="Address" getControlMethods={this.setControlMethods("address")} fields={[
            { key: "address1", label: "Address Line 1", value: appraiser.address1, validation: yup.string().nullable().required("Address is required") },
            { key: "address2", label: "Address Line 2", value: appraiser.address2 },
            { key: "city", label: "City", value: appraiser.city, validation: yup.string().nullable().required("City is required") },
            { key: "state", label: "State", value: { label: appraiser.state, value: appraiser.state }, dataType: DATA_TYPE_DROPDOWN, dataTypeOptions: { options: stateList.map(x => x.stateAbbreviation) }, validation: yup.string().nullable().required("State is required") },
            { key: "zipCode", label: "Zip", value: appraiser.zipCode, validation: yup.string().nullable().required("Zip is required") },
          ]} isEditable={canEditAppraiser} onEditFinished={this.handleMCardSave} onlyEditable={creatingAppraiser}
          /></Grid>
        <Grid size={cellWidth}>
          <MCard title="Contact Methods" getControlMethods={this.setControlMethods("contactMethods")} fields={[
            {
              key: "homePhoneNumber", label: "Home Phone", value: appraiser.homePhoneNumber, dataType: DATA_TYPE_PHONE_NUMBER,
              validation: yup.string().nullable().matches(phoneRegExp, { excludeEmptyString: true, message: "Home Phone is not valid" })
            },
            {
              key: "mobilePhoneNumber", label: "Mobile Phone", value: appraiser.mobilePhoneNumber, dataType: DATA_TYPE_PHONE_NUMBER,
              validation: yup.string().nullable().matches(phoneRegExp, { excludeEmptyString: true, message: "Mobile Phone is not valid" })
            },
            {
              key: "workPhoneNumber", label: "Work Phone", value: appraiser.workPhoneNumber, dataType: DATA_TYPE_PHONE_NUMBER,
              validation: yup.string().nullable().required("Work Phone is required").matches(phoneRegExp, { excludeEmptyString: true, message: "Work Phone is not valid" })
            },
          ]} isEditable={canEditAppraiser} onEditFinished={this.handleMCardSave} onlyEditable={creatingAppraiser}
          /></Grid>
        {/* Hide when no employee id otherwise it will pull calendar entries for logged in user */}
        {appraiser.employeeId && <Grid size={cellWidth === 4 ? 6 : 12}>
          <MCard
            title="Employee Calendar"
            fields={[{
              key: "calendar",
              control: (
                <EmployeeCalendar
                  employeeId={appraiser.employeeId}
                  readOnly={canEditCalendarAsManager ? false : true}
                  allowManagementEdit={canEditCalendarAsManager}
                />
              )
            }]}
          /></Grid>}
        {canViewUserInfo && <Grid size={cellWidth === 4 ? 6 : 12}>
          <MCard title="Company" getControlMethods={this.setControlMethods("company")} fields={[
            { key: "companyName", label: "Company Name", value: appraiser.companyName },
            { key: "managerInternetUserId", label: "Valuation Coordinator", value: { value: appraiser.managerInternetUserId, label: appraiser.appraiserManager }, dataType: DATA_TYPE_DROPDOWN, dataTypeOptions: { options: appraiserManagers?.items?.map(x => ({ label: `${x.firstName} ${x.lastName}`, value: x.internetUserId })) } },
            { key: "username", label: "Mueller Username (and email address)", value: appraiser.username, editable: creatingAppraiser && canEditUserInfo, validation: yup.string().nullable().required("Username is required") },
            { key: "userEmail", label: "Personal Email Address (for reset password)", value: appraiser.userEmail, editable: creatingAppraiser && canEditUserInfo, validation: yup.string().nullable().email("Must be a valid email").required("Email is required") },
          ]} isEditable={canEditUserInfo || canEditAppraiser} onEditFinished={this.handleMCardSave} onlyEditable={creatingAppraiser}
          /></Grid>}
        {!creatingAppraiser && this.props.appraiser && this.props.appraiser.internetUserId &&
          <Grid size={cellWidth === 4 ? 6 : 12}>
            <EmployeeTEAM internetUserId={this.props.appraiser.internetUserId} />
          </Grid>}
        <Grid size={cellWidth === 4 ? 6 : 12}>
          <MCard
            className="appraiserGridCard hideGridHorizontalScroll"
            title="Appraiser County Proficiency"
            fields={this.getAppraiserCountyFields()}
            isEditable={canEditAppraiserCountyProficiency && !!this.state.appraiserCounties} //dont let the user be able to press the edit button if we dont have any data loaded yet or if they don't have the APPRAISER_EDIT right
            singleColumn
            onEditStarted={this.onAppraiserCountyEditStarted}
            shouldCancelEdit={this.shouldCancelEdit}
            onEditCancelled={this.onAppraiserCountyEditCancelled}
            onEditFinished={this.onAppraiserCountyEditFinished}
          /></Grid>
        {this.props.canViewCountyFiles &&
          <Grid size={cellWidth === 4 ? 6 : 12}>
            <AppraiserCountyFileManager appraiserId={appraiser.appraiserId} canEdit={this.props.canUploadCountyFiles} />
          </Grid>}
        {appraiser.appraiserId !== 0 && canViewAppraiserLicense &&
          <Grid size={cellWidth === 4 ? 6 : 12}>
            <MCard title="License and Certification" fields={[
              { key: "licenseand", control: (<RenderLicenses appraiserNumber={appraiser.appraiserNumber} />) }
            ]} isEditable={canEditAppraiserLicense} />
          </Grid>}
        {appraiser.appraiserId !== 0 && canViewAppraiserSignature &&
          <Grid size={cellWidth === 4 ? 6 : 12}>
            <AppraiserSignature appraiserNumber={appraiser.appraiserNumber} />
          </Grid>}
        {!creatingAppraiser && customerAppraiserExclusionMaintenance && <Grid size={cellWidth === 4 ? 6 : 12}>
          <MCard title="Exclusionary List" fields={[
            {
              key: "exclusions", control: (<ExclusionaryList appraiserNumber={appraiser.appraiserNumber} />)
            }]} />
        </Grid>}
        {!creatingAppraiser && !isFetchingInternetUser && internetUser &&
          <Grid size={cellWidth === 4 ? 6 : 12}>
            <LoginActivity loginStatistics={internetUser.loginStatistics} />
          </Grid>}
        {!creatingAppraiser && <Grid size={cellWidth === 4 ? 6 : 12} className="mac-list-cell">
          <AppraiserMacCard appraiserNumber={appraiser.appraiserNumber} />
        </Grid>}
        <Grid size={cellWidth === 4 ? 6 : 12}>
          <MCard title="Employment Info" fields={[
            { key: "experienceStartDate", label: "Experience Start Date", value: validateDate(appraiser.experienceStartDate), dataType: DATA_TYPE_DATE, validation: yup.mixed().required("Experience Start Date is required") },
            { key: "activeDate", label: "Active Date", value: activeDate, dataType: DATA_TYPE_DATE },
            { key: "inactiveDate", label: "Inactive Date", value: inactive, dataType: DATA_TYPE_DATE, editable: canEditInactiveDate },
            { key: "isEmployee", label: "Mueller Employee", value: appraiser.isEmployee, dataType: DATA_TYPE_BOOLEAN, editable: true },
            { key: "payrollEmployeeId", label: "Payroll Employee Id", value: appraiser.payrollEmployeeId },
            { key: "hourlyPayRate", label: "Hourly Rate", value: "$" + appraiser.hourlyPayRate?.toFixed(2), editable: false },
            { key: "payrateType", label: "Pay Rate Type", value: getPayRateTypeLabel(appraiser.payRateType), editable: false },
            { key: "hireDate", label: "Hire Date", value: hireDate, dataType: DATA_TYPE_DATE },
            { key: "weeklyCapacityHours", label: "Weekly Capacity (hours)", value: appraiser.weeklyCapacityHours, dataType: DATA_TYPE_NUMBER },
            { key: "terminatedDate", label: "Terminated Date", value: terminatedDate, dataType: DATA_TYPE_DATE },
            { key: "allowEditsEffectiveDate", label: "Others Can Edit Effective Date", value: allowEditsEffectiveDate, dataType: DATA_TYPE_DATE },
            { key: "allowEditsEndDate", label: "Others Can Edit End Date", value: allowEditsEndDate, dataType: DATA_TYPE_DATE },
          ]} isEditable={canEditAppraiser} onEditFinished={this.handleMCardSave} onlyEditable={creatingAppraiser}
          /></Grid>
        {!creatingAppraiser && canViewUserInfo && <Grid size={cellWidth === 4 ? 6 : 12}>
          {isFetchingInternetUser && <MSpinner />}
          {!isFetchingInternetUser && internetUser &&
            <UserInformation
              userInformation={internetUser}
              internetUserId={internetUser.id}
              canEditUser={canEditUserInfo}
            />}
        </Grid>}
        {appraiser.appraiserId !== 0 &&
          <Grid size={cellWidth === 4 ? 6 : 12}>
            <MCard title="Designations" fields={[
              {
                key: "designations", control: (
                  <RenderDesignations
                    saveDesignation={(newDesignationArray) => {
                      this.handleMCardSave(null, { appraiserDesignationViewModels: newDesignationArray });
                    }}
                    fields={appraiser.appraiserDesignationViewModels}
                  />)
              }]} />
          </Grid>}
        <Grid size={cellWidth === 4 ? 6 : 12}>
          <MCard title="AQB" fields={[
            { key: "conformsToAQBCriteria", label: "Conforms to AQB Criteria", value: appraiser.conformsToAQBCriteria, dataType: DATA_TYPE_BOOLEAN },
            { key: "appraisalSubcommitteeDisciplinaryAction", label: "Appraisal Subcommittee Disciplinary Action", value: appraiser.appraisalSubcommitteeDisciplinaryAction, editable: true },
            { key: "appraisalSubcommitteeDisciplinaryActionEffectiveDate", label: "Effective Date", value: ASDAEffectiveDate, dataType: DATA_TYPE_DATE },
            { key: "appraisalSubcommitteeDisciplinaryActionEndDate", label: "End Date", value: ASDAEndDate, dataType: DATA_TYPE_DATE },
          ]} isEditable={canEditAppraiser} onEditFinished={this.handleMCardSave} onlyEditable={creatingAppraiser}
          /></Grid>
        {appraiser.employeeId && <Grid size={cellWidth === 4 ? 6 : 12}>
          <AdminTimeLog employeeId={appraiser.employeeId} canAddAndEdit={true} />
        </Grid>}
        {creatingAppraiser && appraiser.appraiserId === 0 &&
          <Grid size={12}>
            <div className="link">
              <Button
                style={{ float: "right", marginRight: 20 }} color="secondary"
                className="m-t-10 save"
                onClick={this.backToAppraiserList}
              >
                Cancel
              </Button>
            </div>
            <Button
              style={{ float: "right", marginRight: 20 }}
              type="submit"
              className="m-t-10 save"
              onClick={this.submitClicked}
            >
              Create
            </Button>
          </Grid>}
        {!creatingAppraiser && appraiser.appraiserId !== 0 && canViewUserInfo && <Grid size={cellWidth === 4 ? 6 : 12}>
          <MCard title="Appraiser Order Queue" fields={[
            { key: "appraiserQueue", control: (<AppraiserQueue appraiserId={appraiser.appraiserId} />) }
          ]} />
        </Grid>}
      </Grid>
    );
  }
}

AppraiserProfileEdit.propTypes = {
  verticalDisplay: PropTypes.bool,

  isFetching: PropTypes.bool.isRequired,
  appraiser: PropTypes.object.isRequired,
  appraiserManagers: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,

  stateList: PropTypes.array.isRequired,

  history: PropTypes.object,
  location: PropTypes.object,
  getNewSearchResults: PropTypes.func,
  creatingAppraiser: PropTypes.bool,
  isFetchingInternetUser: PropTypes.bool,
  internetUser: PropTypes.object,

  getAllCounties: PropTypes.func,
  allCounties: PropTypes.array,
  fetchingAllCounties: PropTypes.bool,
  appraiserCounties: PropTypes.array,
  saveAppraiserCounties: PropTypes.func,
  fetchingAppraiserCounties: PropTypes.bool,

  updateHeader: PropTypes.func,
  clearHeader: PropTypes.func,
  showHeaderButton: PropTypes.bool, // Set this to false to hide the "View Order Queue" header button

  canEditAppraiser: PropTypes.bool,
  canEditAppraiserLicense: PropTypes.bool,
  canEditAppraiserCountyProficiency: PropTypes.bool,
  canViewAppraiserSignature: PropTypes.bool,
  canViewAppraiserLicense: PropTypes.bool,
  customerAppraiserExclusionMaintenance: PropTypes.bool,
  canViewUserInfo: PropTypes.bool,
  canEditUserInfo: PropTypes.bool,
  canUploadCountyFiles: PropTypes.bool,
  canViewCountyFiles: PropTypes.bool,
  canEditInactiveDate: PropTypes.bool,
  canEditCalendarAsManager: PropTypes.bool,
};

AppraiserProfileEdit.defaultProps = {
  creatingAppraiser: false,
  showHeaderButton: true,
};

const mapStateToProps = state => {
  return {
    stateList: state.appraiser.stateList,
    appraiserManagers: state.appraiser.appraiserManagers,
    appraiserCounties: state.appraiser.counties,
    fetchingAppraiserCounties: state.appraiser.fetchingCounties,
    internetUser: state.user.internetUser,
    isFetchingInternetUser: state.user.isFetchingInternetUser,
    allCounties: state.region.allCounties,
    fetchingAllCounties: state.region.isFetchingAllCounties,
    isFetching: state.appraiser.fetchingAppraiserProfile,
    appraiser: state.appraiser.appraiserProfile,
    canEditAppraiser: state.user.profile.rights.includes(APPRAISER_EDIT),
    canEditAppraiserLicense: state.user.profile.rights.includes(APPRAISER_LICENSE_EDIT),
    canEditAppraiserCountyProficiency: state.user.profile.rights.includes(APPRAISER_COUNTY_PROFICIENCY_EDIT),
    canViewAppraiserSignature: state.user.profile.rights.includes(APPRAISER_SIGNATURE_VIEW),
    canViewAppraiserLicense: state.user.profile.rights.includes(APPRAISER_LICENSE_VIEW),
    customerAppraiserExclusionMaintenance: state.user.profile.rights.includes(CUSTOMER_APPRAISER_EXCLUSION_MAINTENANCE),
    canViewUserInfo: userHasRight(AUTHENTICATION_VIEW)(state),
    canEditUserInfo: userHasRight(AUTHENTICATION_EDIT)(state),
    canUploadCountyFiles: userHasRight(APPRAISER_COUNTY_FILES_EDIT)(state),
    canViewCountyFiles: userHasRight(APPRAISER_COUNTY_FILES_VIEW)(state),
    canEditInactiveDate: userHasRight(APPRAISER_INACTIVE_EDIT)(state),
    canEditCalendarAsManager: userHasRight(MANAGEMENT_CALENDAR_EDIT)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  updateHeader(headerDefinitions) {
    dispatch(updateHeader(headerDefinitions));
  },
  clearHeader() {
    dispatch(clearHeader());
  },
  getNewSearchResults(searchTerm) {
    dispatch(getNewSearchResults(searchTerm));
  },
  saveAppraiserCounties(appraiserId, counties, callback) {
    dispatch(setAppraiserCounties(appraiserId, counties, callback));
  },
  onSave(item) {
    dispatch(updateAppraiserProfile(item));
  },
  getAllCounties() {
    dispatch(getAllCounties());
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AppraiserProfileEdit));

export const licenseSchema = yup.object({
  licenseState: yup.object().required(),
  licenseNumber: yup.string().required(),
  effectiveDate: yup.string().required(),
  expirationDate: yup.string().required(),
  licenseStatus: yup.string().required(),
  licenseCertificateType: yup.object().required()
});

export const validateDate = dt => {
  if (typeof dt === "string" && dt && dt.length > 0) {
    try {
      const parsedDate = parseISO(dt);
      if (!isValid(parsedDate)) {
        return null;
      }
      const dateString = format(parsedDate, "yyyy");
      if (dateString <= "1969") {
        return null;
      }
      return dt;
    } catch {
      return null;
    }
  }
  return null;
};
